import React, {useEffect, useRef, useState} from 'react'
import TopInformation from '../../components/shared/topinformation/TopInformation.js'
import {useTranslation} from 'react-i18next'
import {useDispatch, useSelector} from 'react-redux'
import UpdateFirmwareTableBody from "../../components/firmware/devicefirmwaretable/UpdateFirmwareTableBody.js"
import {constructPath, findGroup, createPathStringFromArray} from "../../helpers/helperfunctions.js"
import {cloneDeep, difference, uniq} from "lodash"
import DeviceFirmwareTableFilterSearch
    from "../../components/firmware/devicefirmwaretable/DeviceFirmwareTableFilterSearch.js"
import {setFirmwareFilter} from "../../helpers/reduxstore/reducers/filterReducer.js"
import TFooter from "../../components/shared/tfooter/TFooter.js"
import ComboBox from "../../components/shared/combobox/ComboBox.js"
import {Button} from "react-bootstrap"
import InformationModal from "../../components/shared/informationmodal/InformationModal.js"
import {FirmwareNavBar} from "./FirmwareNavBar.js"
import useNavigationGuardByRole from "../../helpers/hooks/useNavigationGuardByRole.js"
import {roleConstants} from "../../helpers/constants.js"
import {offerFirmwareToCustomer, saveFirmwareConfiguration} from "../../helpers/reduxstore/reducers/groupReducer.js"
import {
    getCustomerFirmwareUpdates,
    removeCustomerFirmwareOffer
} from "../../helpers/reduxstore/reducers/customerFirmwareUpdatesReducer.js"
import {getDevices, getFirmwareFilterOptions, getFirmwareList} from "../../api/api.js"
import {queryDeviceList, queryDeviceAggregates} from "../../api/deviceList.js"
import useInterval from "../../helpers/hooks/useInterval.js"
import useSetLocation from "../../helpers/hooks/useSetLocation.js"

/** Renders firmware page */
export default function UpdateFirmwarePage() {
    useNavigationGuardByRole([roleConstants.Admin])
    useSetLocation()

    const {t} = useTranslation(['firmwarepage', 'common'])
    const dispatch = useDispatch()
    const [deviceListLoading, setDeviceListLoading] = useState(true)
    const [deviceListWithCheck, setDeviceListWithCheck] = useState([])
    const [checkedDeviceIdList, setCheckedDeviceIdList] = useState([]) // used to record checked rows
    const [firmwareListFilterOptions, setFirmwareListFilterOptions] = useState([{option: "Firmware version", id: ""}])
    const [deviceTypeFilterOptions, setDeviceTypeFilterOptions] = useState([{option: "Device model", id: ""}])
    const [firmwareListUpdateOptions, setFirmwareListUpdateOptions] = useState([{option: "Firmware version", id: ""}])
    const [updateFirmwareVersion, setUpdateFirmwareVersion] = useState("")
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [showOfferConfirmation, setShowOfferConfirmation] = useState(false)
    const {firmwareFilter} = useSelector(state => state.filter)
    const {allowedGroups} = useSelector(state => state.group)
    const {list: customerFirmwareUpdateList} = useSelector(state => state.customerFirmwareUpdates)
    const [firmwareList, setFirmwareList] = useState([])
    const [oldDeviceFamily, setOldDeviceFamily] = useState('je') // je or aware

    useEffect(() => {
        let firmwareListOptions = firmwareList.map(item => ({option: item.firmware_version + (item.test_version === true ? " (Test)" : ""), id: item.id}))
        firmwareListOptions.sort((a, b) => b.option.localeCompare(a.option))
        setFirmwareListUpdateOptions([{option: "Firmware version", id: ""}, ...firmwareListOptions])
    }, [firmwareList])

    const [deviceList, setDeviceList] = useState([])
    const [deviceListCount, setDeviceListCount] = useState(0)
    const deviceListAbortControllerRef = useRef(new AbortController())
    const aggregatesAbortControllerRef = useRef(new AbortController())

    useEffect(() => {
        dispatch(getCustomerFirmwareUpdates())
    }, [dispatch])

    useEffect(() => {
        handleFirmwareFilterChange()
    }, [firmwareFilter])

    async function handleFirmwareFilterChange() {
        getFirmwareList(firmwareFilter?.deviceFamily ?? "je").then((res) => {
            
            setFirmwareList(res)
        })
        const filterOptions = await getFirmwareFilterOptions(firmwareFilter.filterGroups.map(g => g.id).join(","), firmwareFilter?.deviceFamily ?? "je")
        setFirmwareListFilterOptions([
            {option: "Firmware version", id: ""},
            ...filterOptions.firmwareVersion.filter(o => o).map(o => {
                return {option: o, id: o}
            })])
        await setDeviceTypeFilterOptions([{option: "Device model", id: ""},
            ...filterOptions.model.filter(o => o).map(o => {
                return {option: o, id: o}
            })])
        if (oldDeviceFamily !== firmwareFilter.deviceFamily) { // when family is changed, checked devices are reset
            setUpdateFirmwareVersion("")
            setCheckedDeviceIdList([])
        }
        setOldDeviceFamily(firmwareFilter.deviceFamily)

        setDeviceListLoading(true)
        if (deviceListAbortControllerRef.current && deviceListAbortControllerRef.current.signal)
            deviceListAbortControllerRef.current.abort("Query renewed")
        if (aggregatesAbortControllerRef.current && aggregatesAbortControllerRef.current.signal)
            aggregatesAbortControllerRef.current.abort("Query renewed")
        deviceListAbortControllerRef.current = new AbortController()
        aggregatesAbortControllerRef.current = new AbortController()
        dispatchQueryDevices()
    }

    useEffect(() => {
        if (deviceList) {
            const newDeviceListWithCheck = cloneDeep(deviceList).map(device => deviceWithPathAndCheck(device))
            for (const deviceId of checkedDeviceIdList) {
                const foundDevice = newDeviceListWithCheck.find(d => d.id === deviceId)
                if (foundDevice) foundDevice.checked = true
            }
            setDeviceListWithCheck(newDeviceListWithCheck)
        }
    }, [deviceList])

    // poll devices and update state if firmwareStatus has changed (or any new devices)
    //  We do not want to update layout all the time because it flickers when there are filters
    useInterval(async () => {
        const remoteDeviceList = await getDevices(deviceListWithCheck.map((d) => d.id))
        const hasChanged = deviceListWithCheck.some((orig) => {
            const remote = remoteDeviceList.find((remote) => remote.id === orig.id)
            if (!remote) return true
            return remote.device_twin.reported.firmwareStatus !== orig.device_twin.reported.firmwareStatus
        })
        if (hasChanged) {
            console.log("device list has changed!")
            dispatchQueryDevices().then()
        }
    }, 1000 * 15)

    function dispatchQueryDevices() {
        const filter = {
            skip: (firmwareFilter.page - 1) * firmwareFilter.rowsPerPage,
            take: firmwareFilter.rowsPerPage,
            model: firmwareFilter.deviceTypeFilter,
            firmware_version: firmwareFilter.firmwareVersionFilter,
            search: encodeURIComponent(firmwareFilter.searchString ?? ''),
            groups: firmwareFilter.filterGroups.map(g => g.id).join(","),
            sort_direction: firmwareFilter?.deviceListSort?.sortDirection ?? "asc",
            sort_value: firmwareFilter?.deviceListSort?.sortValue ?? "serial_number",
            device_family: firmwareFilter?.deviceFamily ?? "je"
        }

        queryDeviceList(filter, deviceListAbortControllerRef.current.signal)
            .then(res => {
                const rawList = Array.isArray(res) ? res : (res?.deviceList || res?.list || [])
                setDeviceList(rawList)
                setDeviceListLoading(false)
            })
            .catch(e => {
                if (e.message.includes("The user aborted a request") || e.message.includes("Query renewed")) {
                    console.warn("Request aborted or renewed: ", e.message)
                } else {
                    setDeviceListLoading(false)
                    console.error("Error fetching device list: ", e)
                }
            })

        queryDeviceAggregates(filter, aggregatesAbortControllerRef.current.signal)
            .then(res => {
                setDeviceListCount(res?.count ?? 0)
            })
            .catch(e => {
                if (e.message.includes("The user aborted a request") || e.message.includes("Query renewed")) {
                    console.warn("Aggregates request aborted or renewed: ", e.message)
                } else {
                    console.error("Error fetching aggregates: ", e)
                }
            })
    }

    function deviceWithPathAndCheck(device) {
        device.path = createPathStringFromArray(constructPath(findGroup(device.parent, allowedGroups), allowedGroups), device.name)
        device.checked = false
        return device
    }

    const sortHandler = (headerData) => {
        dispatch(setFirmwareFilter({
            deviceListSort: {
                sortDirection: headerData.sortValue === firmwareFilter?.deviceListSort?.sortValue && firmwareFilter?.deviceListSort?.sortDirection === 'asc' ? 'desc' : "asc",
                sortValue: headerData.sortValue
            }
        }))
    }

    const dispatchSetFirmwareRowsPerPage = (rowsPerPage) => {
        dispatch(setFirmwareFilter({rowsPerPage: rowsPerPage, page: 1}))
    }

    function toggleRowChecked(row, value) {
        const newDeviceList = cloneDeep(deviceListWithCheck)
        newDeviceList.find(d => d.id === row.id).checked = value
        setCheckedDeviceIdList(value ? [...checkedDeviceIdList, row.id] : checkedDeviceIdList.filter(id => id !== row.id))
        setDeviceListWithCheck(newDeviceList)
    }

    function toggleAllShownRows() {
        const newDeviceList = cloneDeep(deviceListWithCheck)
        const allChecked = deviceListWithCheck.every(d => d.checked)
        if (!allChecked) setCheckedDeviceIdList(uniq([...checkedDeviceIdList, ...newDeviceList.map(d => d.id)]))
        else setCheckedDeviceIdList(difference(checkedDeviceIdList, newDeviceList.map(d => d.id)))
        for (const device of newDeviceList) {
            device.checked = !allChecked
        }
        setDeviceListWithCheck(newDeviceList)
    }

    const commitChanges = async () => {
        setShowConfirmation(false)
        await dispatch(saveFirmwareConfiguration(updateFirmwareVersion, checkedDeviceIdList))
        setCheckedDeviceIdList([])
    }

    const commitOfferChanges = async () => {
        setShowOfferConfirmation(false)
        await dispatch(offerFirmwareToCustomer(updateFirmwareVersion, checkedDeviceIdList))
        await dispatch(getCustomerFirmwareUpdates())
        setCheckedDeviceIdList([])
    }

    const handleRemoveCustomerOffer = (deviceId) => {
        dispatch(removeCustomerFirmwareOffer([deviceId]))
    }

    const setFilterGroups = (groupIdList) => {
        dispatch(setFirmwareFilter({
            filterGroups: groupIdList,
            firmwareVersionFilter: "",
            deviceTypeFilter: "",
            page: 1
        }))
    }

    /** @param {int} page */
    function setPage(page) {
        dispatch(setFirmwareFilter({page}))
    }

    return (
        <React.Fragment>
            <InformationModal show={showConfirmation} title={t('firmwarepage:modal.confirm.title')}
                              body={t('firmwarepage:modal.confirm.text', {
                                  count: checkedDeviceIdList.length,
                                  version: firmwareListUpdateOptions.find(f => f.id === updateFirmwareVersion)?.option
                              })} dismiss={() => setShowConfirmation(false)} accept={commitChanges}/>

            <InformationModal show={showOfferConfirmation} title={t('firmwarepage:modal.confirm_offer.title')}
                              body={t('firmwarepage:modal.confirm_offer.text', {
                                  count: checkedDeviceIdList.length,
                                  version: firmwareListUpdateOptions.find(f => f.id === updateFirmwareVersion)?.option
                              })} dismiss={() => setShowOfferConfirmation(false)} accept={commitOfferChanges}/>
            <div className="d-flex flex-column" style={{flex: '1'}}>

                <TopInformation title={t('firmwarepage:info_section.info_section_title')}
                                subTitle={t('firmwarepage:info_section.info_section_p')}/>

                <FirmwareNavBar currentPage={'update'}/>

                <DeviceFirmwareTableFilterSearch className="d-block d-md-flex pt-4 pb-2 flex-wrap"
                                                 filterGroups={firmwareFilter.filterGroups}
                                                 setFilterGroups={setFilterGroups}
                                                 firmwareListOptions={firmwareListFilterOptions}
                                                 firmwareVersionFilterValue={firmwareFilter.firmwareVersionFilter}
                                                 deviceTypeFilterValue={firmwareFilter.deviceTypeFilter}
                                                 deviceTypeFilterOptions={deviceTypeFilterOptions}
                                                 deviceFamily={firmwareFilter?.deviceFamily ?? "je"}
                />

                <div className={'mb-2'}>
                <span
                    className="text-nowrap small">{`${t('firmwarepage:updatetable.headers.total')} ${deviceListCount}`}</span>
                </div>

                <UpdateFirmwareTableBody className={'mb-2'}
                                         deviceList={deviceListWithCheck}
                                         deviceListLoading={deviceListLoading}
                                         sort={firmwareFilter.deviceListSort}
                                         sortHandler={sortHandler}
                                         toggleRowChecked={toggleRowChecked}
                                         toggleAllShownRows={toggleAllShownRows}
                                         customerFirmwareUpdateList={customerFirmwareUpdateList}
                                         removeCustomerOffer={handleRemoveCustomerOffer}/>
                <TFooter
                    className="mb-3"
                    setPage={setPage}
                    rowsPerPage={firmwareFilter.rowsPerPage}
                    setRowsPerPage={dispatchSetFirmwareRowsPerPage}
                    dataLength={deviceListCount}
                    page={firmwareFilter.page}/>

                <div className="d-flex pb-3 align-items-center">
                    <span className="pe-2 text-nowrap">Choose version:</span>
                    <ComboBox className={''} inputValue={(e) => setUpdateFirmwareVersion(e.target.value)}
                              value={updateFirmwareVersion} options={firmwareListUpdateOptions} hasLabel={false}/>

                    <Button className={'mx-2'} variant="primary" onClick={() => setShowConfirmation(true)}
                            disabled={updateFirmwareVersion === "" || checkedDeviceIdList.length === 0}
                            type="submit">{t('firmwarepage:buttons.update_devices', {count: checkedDeviceIdList.length})}
                    </Button>

                    <Button className={'mx-2'} variant="outline-primary" onClick={() => setShowOfferConfirmation(true)}
                            disabled={updateFirmwareVersion === "" || checkedDeviceIdList.length === 0}
                            type="button">{t('firmwarepage:buttons.offer_to_customer', {count: checkedDeviceIdList.length})}
                    </Button>
                </div>
            </div>
        </React.Fragment>
    )
}
